import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  Plus,
  Trash2,
  Sparkles,
  Loader2,
  Send,
  EyeOff,
  Edit3,
  Save,
  ExternalLink,
  CheckCircle2,
  Star,
  X,
  ChevronRight,
  ChevronDown,
  CheckSquare,
  Square,
  Search,
  Calendar,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import PromptEditor from "@/components/PromptEditor";
import MarkdownEditor from "@/components/MarkdownEditor";
import ReactMarkdown from "react-markdown";
import ConfirmDialog from "@/components/ConfirmDialog";
import CategoryManager from "@/components/CategoryManager";
import PublishTargetModal from "@/components/PublishTargetModal";

const StatusBadge = ({ n }) => {
  const isDraft = n.status !== "published";
  return isDraft ? (
    <span className="badge-neutral !bg-amber-50 !text-amber-700 font-semibold text-[10px]">BOZZA</span>
  ) : (
    <span className="badge-pos flex items-center gap-1 text-[10px]"><CheckCircle2 size={10} /> Pubblicata</span>
  );
};

export default function AdminNews() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", category: "Mercato", status: "published", visibility: "all", target_client_ids: [] });
  const [busy, setBusy] = useState(false);
  const [whenDays, setWhenDays] = useState(3);  // finestra temporale AI fetch (giorni)
  const [confirmState, setConfirmState] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Publish modal state (single or bulk)
  const [publishModal, setPublishModal] = useState(null); // { mode: 'single'|'bulk', item?: news }
  // Section collapse state (persisted in localStorage)
  const [openDrafts, setOpenDrafts] = useState(() => localStorage.getItem("adyboAdminNews_openDrafts") !== "0");
  const [openPub, setOpenPub] = useState(() => localStorage.getItem("adyboAdminNews_openPub") !== "0");
  const [showAllPublished, setShowAllPublished] = useState(false);
  // Filtri per la sezione Pubblicate (testo + data)
  const [pubSearch, setPubSearch] = useState("");
  const [pubDate, setPubDate] = useState(""); // YYYY-MM-DD
  // Modal state
  const [openId, setOpenId] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // "view" | "edit"
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCategory, setEditCategory] = useState("");
  // Categories list (used by datalist combobox)
  const [categories, setCategories] = useState([]);

  const load = async () => { const { data } = await api.get("/news"); setItems(data); };
  const loadCategories = async () => {
    try {
      const { data } = await api.get("/news/categories");
      setCategories(data);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); loadCategories(); }, []);

  useEffect(() => { localStorage.setItem("adyboAdminNews_openDrafts", openDrafts ? "1" : "0"); }, [openDrafts]);
  useEffect(() => { localStorage.setItem("adyboAdminNews_openPub", openPub ? "1" : "0"); }, [openPub]);

  const create = async () => {
    if (!form.title || !form.body) return toast.error("Compila titolo e testo");
    try {
      // Se l'utente vuole pubblicare subito, crea prima come bozza e poi apri la
      // maschera destinatari per scegliere pubblico + notifiche/email.
      if (form.status === "published") {
        const { data: created } = await api.post("/news", { ...form, status: "draft" });
        toast.success("News creata come bozza — scegli destinatari per pubblicare");
        // Reset del form prima di aprire la modale (l'utente può continuare dopo)
        const createdTitle = form.title;
        const createdBody = form.body;
        setForm({ title: "", body: "", category: "Mercato", status: "published", visibility: "all", target_client_ids: [] });
        await load();
        loadCategories();
        // Apri la maschera di pubblicazione
        setPublishModal({
          mode: "single",
          item: { ...created, title: createdTitle, body: createdBody },
          onConfirm: async ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message, email_client_ids, email_company_ids, email_group_id }) => {
            try {
              await api.post(`/news/${created.id}/publish`, {
                send_notification,
                send_email,
                email_subject,
                email_message,
                visibility: target === "companies" ? "companies" : target === "users" ? "specific" : "all",
                target_company_ids: target === "companies" ? company_ids : [],
                target_client_ids: target === "users" ? client_ids : [],
                email_client_ids: email_client_ids || [],
                email_company_ids: email_company_ids || [],
                email_group_id: email_group_id || null,
              });
              toast.success(
                "News pubblicata" +
                  (target === "companies"
                    ? ` a ${company_ids.length} aziend${company_ids.length === 1 ? "a" : "e"}`
                    : target === "users"
                    ? ` a ${client_ids.length} utent${client_ids.length === 1 ? "e" : "i"}`
                    : " a tutti i clienti")
              );
              setPublishModal(null);
              await load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore");
            }
          },
        });
      } else {
        await api.post("/news", form);
        toast.success("News creata");
        setForm({ title: "", body: "", category: "Mercato", status: "published", visibility: "all", target_client_ids: [] });
        load();
        loadCategories();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    }
  };

  const fetchAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/news/ai/fetch", { max_per_topic: 5, when_days: whenDays });
      if (data.status === "processing") {
        // Backend now runs the AI generation as a fire-and-forget background task
        // (K8s ingress cuts requests at ~60s). We poll the news list for ~2 minutes
        // and refresh whenever new AI drafts appear.
        toast.info("Generazione AI news avviata: le bozze compariranno tra 1-2 minuti…", { duration: 8000 });
        const beforeCount = items.filter((n) => n.ai_generated && n.status === "draft").length;
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const { data: newList } = await api.get("/news");
            setItems(newList);
            const currCount = (newList || []).filter((n) => n.ai_generated && n.status === "draft").length;
            const delta = currCount - beforeCount;
            if (delta > 0) {
              toast.success(`${delta} nuove news AI create come bozza`);
              return;
            }
            if (attempts < 12) {
              setTimeout(poll, 10_000); // retry every 10s, up to 2 minutes
            } else {
              toast.warning("La generazione AI sta impiegando più tempo del previsto. Ricarica la pagina fra qualche minuto.");
            }
          } catch (_e) {
            // ignore, retry
            if (attempts < 12) setTimeout(poll, 10_000);
          }
        };
        setTimeout(poll, 15_000); // first poll after 15s
      } else if (typeof data.created === "number") {
        // Backwards-compat: old sync response
        if (data.created > 0) {
          toast.success(`${data.created} news AI create come bozza`);
        } else {
          toast.warning(
            `AI ha analizzato ${data.raw_count || 0} articoli grezzi ma nessuno ha superato i criteri di rilevanza. Riprova più tardi o allarga il periodo.`,
            { duration: 6000 }
          );
        }
        load();
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally { setBusy(false); }
  };

  const openModal = (n, mode = "view") => {
    setOpenId(n.id);
    setModalMode(mode);
    setEditTitle(n.title || "");
    setEditBody(n.body || "");
    setEditCategory(n.category || "Mercato");
  };
  const closeModal = () => { setOpenId(null); setModalMode("view"); };

  const saveEdit = async () => {
    const cur = items.find((x) => x.id === openId);
    if (!cur) return;
    try {
      await api.patch(`/news/${openId}`, {
        title: editTitle,
        body: editBody,
        category: editCategory,
        status: cur.status,
        visibility: cur.visibility || "all",
        target_client_ids: cur.target_client_ids || [],
        publication_date: cur.publication_date || null,
        image_url: cur.image_url || null,
        is_featured: !!cur.is_featured,
      });
      toast.success("Modifiche salvate");
      await load();
      await loadCategories();
      setModalMode("view");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const publish = (n) => {
    setPublishModal({
      mode: "single",
      item: n,
      onConfirm: async ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message, email_client_ids, email_company_ids, email_group_id }) => {
        try {
          await api.post(`/news/${n.id}/publish`, {
            send_notification,
            send_email,
            email_subject,
            email_message,
            visibility: target === "companies" ? "companies" : target === "users" ? "specific" : "all",
            target_company_ids: target === "companies" ? company_ids : [],
            target_client_ids: target === "users" ? client_ids : [],
            email_client_ids: email_client_ids || [],
            email_company_ids: email_company_ids || [],
            email_group_id: email_group_id || null,
          });
          toast.success(
            "News pubblicata" +
              (target === "companies"
                ? ` a ${company_ids.length} aziend${company_ids.length === 1 ? "a" : "e"}`
                : target === "users"
                ? ` a ${client_ids.length} utent${client_ids.length === 1 ? "e" : "i"}`
                : " a tutti i clienti")
          );
          setPublishModal(null);
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore");
        }
      },
    });
  };
  const unpublish = async (id) => { await api.post(`/news/${id}/unpublish`); load(); };
  const del = (n) => {
    setConfirmState({
      title: "Eliminare la news?",
      message: `"${n.title}" verrà rimossa definitivamente.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/news/${n.id}`);
          toast.success("News eliminata");
          if (openId === n.id) closeModal();
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const toggleFeature = async (n) => {
    const next = !n.is_featured;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_featured: next } : x)));
    try {
      await api.post(`/news/${n.id}/feature`, { is_featured: next });
      toast.success(next ? "News in evidenza" : "Evidenza rimossa");
    } catch (e) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_featured: !next } : x)));
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const drafts = items.filter((n) => n.status !== "published");
  const publishedAll = items.filter((n) => n.status === "published");
  // Filtro "mostra solo oggi + ieri" (default) per non ingombrare la vista con news vecchie.
  // Il confronto è basato su publication_date (YYYY-MM-DD, timezone-independent).
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const yesterday = new Date(today.getTime() - 86400000);
  const yesterdayKey = yesterday.toISOString().slice(0, 10);
  const recentKeys = new Set([todayKey, yesterdayKey]);
  // Applica filtri testuali e per data alla sezione Pubblicate.
  // Se testo o data sono valorizzati, il filtro "oggi+ieri" viene bypassato.
  const searchTerm = pubSearch.trim().toLowerCase();
  const hasActiveFilter = !!searchTerm || !!pubDate;
  const published = publishedAll.filter((n) => {
    // 1) Filtro data specifica (se attivo)
    if (pubDate && (n.publication_date || "").slice(0, 10) !== pubDate) return false;
    // 2) Filtro testo (title, body, category)
    if (searchTerm) {
      const hay = `${n.title || ""} ${n.body || ""} ${n.category || ""}`.toLowerCase();
      if (!hay.includes(searchTerm)) return false;
    }
    // 3) Se nessun filtro attivo, applica "oggi+ieri" salvo showAllPublished
    if (!hasActiveFilter && !showAllPublished) {
      return recentKeys.has((n.publication_date || "").slice(0, 10));
    }
    return true;
  });
  const publishedOlderCount = publishedAll.length - publishedAll.filter((n) => recentKeys.has((n.publication_date || "").slice(0, 10))).length;
  const openNews = openId ? items.find((x) => x.id === openId) : null;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (list) => {
    const ids = list.map((n) => n.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = () => {
    if (selected.size === 0) return;
    setConfirmState({
      title: `Eliminare ${selected.size} news?`,
      message: "Le news selezionate verranno rimosse definitivamente.",
      confirmLabel: "Elimina tutte",
      variant: "danger",
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/news/bulk-delete", { ids: Array.from(selected) });
          toast.success(`${data.deleted} news eliminate`);
          clearSelection();
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };
  const bulkPublish = () => {
    if (selected.size === 0) return;
    setPublishModal({
      mode: "bulk",
      onConfirm: async ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message, email_client_ids, email_company_ids, email_group_id }) => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/news/bulk-publish", {
            ids: Array.from(selected),
            send_notification,
            send_email,
            email_subject,
            email_message,
            visibility: target === "companies" ? "companies" : target === "users" ? "specific" : "all",
            target_company_ids: target === "companies" ? company_ids : [],
            target_client_ids: target === "users" ? client_ids : [],
            email_client_ids: email_client_ids || [],
            email_company_ids: email_company_ids || [],
            email_group_id: email_group_id || null,
          });
          toast.success(`${data.published} news pubblicate`);
          clearSelection();
          setPublishModal(null);
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore pubblicazione");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gestione News</h1>
        <p className="text-sm text-slate-500 mt-1">News manuali o generate dall&apos;AI dai principali temi energetici e geopolitici.</p>
      </div>

      <div className="card-elevated p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center shadow-lg shadow-purple-500/40 flex-shrink-0">
            <Sparkles className="text-white" size={26} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg text-slate-900">News AI dal web</div>
            <div className="text-sm text-slate-500">Aggrega ultime notizie su gas TTF/PSV, energia, petrolio, GNL, CO2 e geopolitica, rielaborate in sintesi italiana. Salvate come bozze.</div>
          </div>
          <div className="flex flex-col items-start gap-1 min-w-[220px]">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              Finestra temporale
              <span className="text-purple-600 font-bold normal-case tracking-normal">{whenDays} giorn{whenDays === 1 ? "o" : "i"}</span>
            </label>
            <div className="flex items-center gap-2 w-full">
              {[2, 3, 5, 7].map((d) => (
                <button
                  key={d}
                  onClick={() => setWhenDays(d)}
                  disabled={busy}
                  className={`flex-1 py-1 px-2 text-xs font-semibold rounded-lg border transition ${
                    whenDays === d
                      ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-purple-400 hover:text-purple-700"
                  }`}
                  data-testid={`news-ai-when-${d}`}
                >
                  {d}g
                </button>
              ))}
            </div>
          </div>
          <button onClick={fetchAI} disabled={busy} className="btn-brand flex items-center gap-2" data-testid="news-ai-fetch">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {busy ? "Aggregazione…" : "Aggrega news AI"}
          </button>
        </div>
      </div>

      {/* Prompt editor visibile solo agli admin */}
      {isAdmin && <PromptEditor promptKey="news_editor" />}

      {/* Categorie management */}
      <CategoryManager
        endpoint="/news/categories"
        title="Categorie news"
        subtitle="Le nuove categorie diventano subito filtri per i clienti nella pagina News."
        onChange={loadCategories}
        confirmDelete={setConfirmState}
      />

      <details className="card-elevated p-5" open={items.length === 0}>
        <summary className="font-display font-semibold cursor-pointer flex items-center gap-2"><Plus size={16} /> Crea news manuale</summary>
        <div className="mt-4 space-y-3">
          <input placeholder="Titolo" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="news-title" />
          <MarkdownEditor
            value={form.body}
            onChange={(v) => setForm({ ...form, body: v })}
            rows={8}
            placeholder="Testo della news — usa la toolbar per grassetto, titoli, liste, link"
            testid="news-body-editor"
          />
          <div className="flex gap-2">
            <input list="news-cat-list-create" placeholder="Categoria" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="news-category-input" />
            <datalist id="news-cat-list-create">
              {categories.map((c) => <option key={c.name} value={c.name} />)}
            </datalist>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500" data-testid="news-status-select">
              <option value="published">Pubblica</option>
              <option value="draft">Bozza</option>
            </select>
          </div>
          <button onClick={create} className="btn-brand flex items-center gap-2" data-testid="news-create"><Plus size={16} /> Salva</button>
        </div>
      </details>

      {/* Bulk action toolbar (visibile solo se ci sono selezionate) */}
      {selected.size > 0 && (
        <div className="sticky top-16 z-30 card-elevated p-3 flex items-center gap-3 flex-wrap border-2 border-blue-200 bg-blue-50/70 backdrop-blur-sm" data-testid="news-bulk-toolbar">
          <span className="text-sm font-semibold text-blue-900">
            {selected.size} selezionat{selected.size === 1 ? "a" : "e"}
          </span>
          <button
            onClick={bulkPublish}
            disabled={bulkBusy}
            className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-60"
            data-testid="news-bulk-publish"
          >
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Pubblica selezionate
          </button>
          <button
            onClick={bulkDelete}
            disabled={bulkBusy}
            className="text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-60"
            data-testid="news-bulk-delete"
          >
            <Trash2 size={14} /> Elimina selezionate
          </button>
          <button
            onClick={clearSelection}
            className="text-sm text-slate-600 hover:text-slate-900 ml-auto flex items-center gap-1"
            data-testid="news-bulk-clear"
          >
            <X size={14} /> Deseleziona tutto
          </button>
        </div>
      )}

      {/* Drafts section — compact collapsible list */}
      <NewsSection
        title="Bozze"
        count={drafts.length}
        open={openDrafts}
        onToggle={() => setOpenDrafts((v) => !v)}
        items={drafts}
        onOpen={openModal}
        onPublish={publish}
        onUnpublish={unpublish}
        onDelete={del}
        onToggleFeature={toggleFeature}
        selected={selected}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={() => toggleSelectAll(drafts)}
        testid="drafts"
      />

      {/* Published section — barra filtri (testo + data) + toggle scope */}
      {publishedAll.length > 0 && (
        <div className="card-elevated p-3 -mb-2 flex items-center gap-2 flex-wrap" data-testid="news-published-filter">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={pubSearch}
              onChange={(e) => setPubSearch(e.target.value)}
              placeholder="Cerca nelle news pubblicate…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-blue-500 bg-white"
              data-testid="news-search-input"
            />
          </div>
          <div className="relative">
            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="date"
              value={pubDate}
              onChange={(e) => setPubDate(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-blue-500 bg-white"
              data-testid="news-date-input"
            />
          </div>
          {(pubSearch || pubDate) && (
            <button
              onClick={() => { setPubSearch(""); setPubDate(""); }}
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-red-400 hover:text-red-600 text-slate-600 font-semibold bg-white flex items-center gap-1.5"
              data-testid="news-filter-reset"
            >
              <X size={13} /> Reset filtri
            </button>
          )}
          {!hasActiveFilter && (
            <button
              onClick={() => setShowAllPublished((v) => !v)}
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-600 font-semibold bg-white flex items-center gap-1.5 ml-auto"
              data-testid="news-toggle-published-scope"
            >
              {showAllPublished
                ? `Mostra solo recenti (oggi + ieri)`
                : `Mostra tutte (${publishedAll.length}${publishedOlderCount > 0 ? ` · +${publishedOlderCount} più vecchie` : ""})`}
            </button>
          )}
        </div>
      )}

      {/* Published section — compact collapsible list */}
      <NewsSection
        title={
          pubDate
            ? `Pubblicate — ${pubDate}`
            : searchTerm
            ? `Pubblicate — risultati ricerca`
            : showAllPublished
            ? "Pubblicate — tutte"
            : "Pubblicate — oggi + ieri"
        }
        count={published.length}
        open={openPub}
        onToggle={() => setOpenPub((v) => !v)}
        items={published}
        onOpen={openModal}
        onPublish={publish}
        onUnpublish={unpublish}
        onDelete={del}
        onToggleFeature={toggleFeature}
        selected={selected}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={() => toggleSelectAll(published)}
        testid="published"
      />

      {/* Empty state quando i filtri Pubblicate non producono risultati */}
      {publishedAll.length > 0 && published.length === 0 && hasActiveFilter && (
        <div className="card-elevated p-6 text-center text-sm text-slate-500" data-testid="news-published-empty">
          Nessuna news pubblicata corrisponde ai filtri applicati.
          <button
            onClick={() => { setPubSearch(""); setPubDate(""); }}
            className="ml-2 text-blue-600 hover:underline font-semibold"
          >
            Reset filtri
          </button>
        </div>
      )}

      {/* View/edit modal */}
      {openNews && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={closeModal} data-testid="news-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {openNews.is_featured && (
                    <span className="badge-neutral !bg-amber-100 !text-amber-800 font-semibold flex items-center gap-1 text-[10px]">
                      <Star size={10} className="fill-amber-500 text-amber-500" /> IN EVIDENZA
                    </span>
                  )}
                  {openNews.ai_generated && (
                    <span className="badge-neutral !bg-purple-50 !text-purple-700 flex items-center gap-1 text-[10px]"><Sparkles size={10} /> AI</span>
                  )}
                  <StatusBadge n={openNews} />
                  <span className="text-xs text-slate-500">{fmtDateTime(openNews.publication_date)}</span>
                </div>
                {modalMode === "edit" ? (
                  <>
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="mt-2 w-full font-display font-bold text-lg text-slate-900 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
                      data-testid="news-modal-title"
                    />
                    <input
                      list="news-cat-list-edit"
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      placeholder="Categoria"
                      className="mt-2 w-56 text-sm text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
                      data-testid="news-modal-category"
                    />
                    <datalist id="news-cat-list-edit">
                      {categories.map((c) => <option key={c.name} value={c.name} />)}
                    </datalist>
                  </>
                ) : (
                  <h3 className="mt-1 font-display font-bold text-lg text-slate-900">{openNews.title}</h3>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {modalMode === "view" ? (
                  <button onClick={() => setModalMode("edit")} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1.5" data-testid="news-modal-edit">
                    <Edit3 size={14} /> Modifica
                  </button>
                ) : (
                  <button onClick={saveEdit} className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5" data-testid="news-modal-save">
                    <Save size={14} /> Salva
                  </button>
                )}
                {openNews.status !== "published" && (
                  <button onClick={() => publish(openNews)} className="btn-brand text-sm !py-1.5 !px-3 flex items-center gap-1.5" data-testid="news-modal-publish">
                    <Send size={14} /> Pubblica
                  </button>
                )}
                <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-600" data-testid="news-modal-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {modalMode === "edit" ? (
                <MarkdownEditor
                  value={editBody}
                  onChange={setEditBody}
                  rows={14}
                  placeholder="Testo della news"
                  testid="news-modal-body"
                />
              ) : (
                <div className="prose prose-slate prose-sm max-w-none">
                  <ReactMarkdown>{openNews.body || ""}</ReactMarkdown>
                </div>
              )}
              {openNews.source_url && (
                <a href={openNews.source_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600" data-testid={`news-source-${openNews.id}`}>
                  <ExternalLink size={12} /> Fonte: {openNews.source_name || openNews.source_title || "Vedi originale"}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <PublishTargetModal
        open={!!publishModal}
        title={publishModal?.mode === "bulk" ? `Pubblica ${selected.size} news` : "Pubblica news"}
        message={
          publishModal?.mode === "bulk"
            ? "Scegli chi potrà vedere le news selezionate."
            : publishModal?.item
            ? `"${publishModal.item.title}" — scegli il pubblico destinatario.`
            : undefined
        }
        defaultEmailSubject={
          publishModal?.mode === "single" && publishModal?.item
            ? `Nuova news: ${publishModal.item.title}`
            : ""
        }
        defaultEmailMessage={
          publishModal?.mode === "single" && publishModal?.item
            ? (publishModal.item.body?.trim() || publishModal.item.title)
            : ""
        }
        onCancel={() => setPublishModal(null)}
        onConfirm={publishModal?.onConfirm || (() => {})}
      />
    </div>
  );
}

function NewsSection({ title, count, open, onToggle, items, onOpen, onPublish, onUnpublish, onDelete, onToggleFeature, selected, onToggleSelect, onToggleSelectAll, testid }) {
  if (count === 0) return null;
  const allSelected = items.length > 0 && items.every((n) => selected?.has(n.id));
  const someSelected = items.some((n) => selected?.has(n.id));
  return (
    <div className="card-elevated overflow-hidden" data-testid={`news-section-${testid}`}>
      <div className="w-full px-5 py-3 border-b border-slate-100 font-display font-semibold text-slate-700 flex items-center justify-between hover:bg-slate-50/60 transition-colors">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 text-left"
          data-testid={`news-section-toggle-${testid}`}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {title}
          <span className="text-xs font-normal text-slate-400">({count})</span>
        </button>
        {open && onToggleSelectAll && (
          <button
            onClick={onToggleSelectAll}
            className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 font-normal"
            data-testid={`news-section-select-all-${testid}`}
            title={allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
          >
            {allSelected ? <CheckSquare size={14} className="text-blue-600" /> : someSelected ? <CheckSquare size={14} className="text-blue-400" /> : <Square size={14} />}
            {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
          </button>
        )}
      </div>
      {open && (
        <ul className="divide-y divide-slate-100">
          {items.map((n) => (
            <NewsRow
              key={n.id}
              n={n}
              onOpen={onOpen}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              onDelete={onDelete}
              onToggleFeature={onToggleFeature}
              isSelected={selected?.has(n.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NewsRow({ n, onOpen, onPublish, onUnpublish, onDelete, onToggleFeature, isSelected, onToggleSelect }) {
  const isDraft = n.status !== "published";
  const isFeatured = !!n.is_featured;
  return (
    <li
      className={`px-5 py-3 flex items-center gap-3 hover:bg-slate-50/60 cursor-pointer group ${isFeatured ? "bg-amber-50/30" : ""} ${isSelected ? "bg-blue-50/70" : ""}`}
      onClick={() => onOpen(n, "view")}
      data-testid={`admin-news-${n.id}`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect?.(n.id); }}
        className="flex-shrink-0 text-slate-400 hover:text-blue-600 transition-colors"
        aria-label={isSelected ? "Deseleziona" : "Seleziona"}
        data-testid={`news-select-${n.id}`}
      >
        {isSelected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
      </button>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {isFeatured && <Star size={13} className="fill-amber-500 text-amber-500" />}
        {n.ai_generated && <Sparkles size={11} className="text-purple-500" />}
      </div>
      <StatusBadge n={n} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 line-clamp-1">{n.title}</div>
        <div className="text-[11px] text-slate-500">{n.category || "Generale"} · {fmtDate(n.publication_date)}</div>
      </div>
      <div
        className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        {!isDraft && (
          <button
            onClick={() => onToggleFeature(n)}
            className={`p-1.5 rounded-md border transition-colors ${isFeatured ? "bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100" : "border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-500"}`}
            title={isFeatured ? "Rimuovi dall'evidenza" : "Metti in evidenza"}
            data-testid={`news-feature-toggle-${n.id}`}
          >
            <Star size={13} className={isFeatured ? "fill-current" : ""} />
          </button>
        )}
        <button onClick={() => onOpen(n, "edit")} className="p-1.5 text-slate-500 hover:text-blue-600" title="Modifica" data-testid={`news-edit-${n.id}`}>
          <Edit3 size={14} />
        </button>
        {isDraft ? (
          <button onClick={() => onPublish(n)} className="p-1.5 text-emerald-600 hover:text-emerald-700" title="Pubblica" data-testid={`news-publish-${n.id}`}>
            <Send size={14} />
          </button>
        ) : (
          <button onClick={() => onUnpublish(n.id)} className="p-1.5 text-amber-600 hover:text-amber-700" title="Ritira" data-testid={`news-unpublish-${n.id}`}>
            <EyeOff size={14} />
          </button>
        )}
        <button onClick={() => onDelete(n)} className="p-1.5 text-red-500 hover:text-red-700" title="Elimina" data-testid={`news-delete-${n.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <ChevronRight size={15} className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
    </li>
  );
}
